import React, { useMemo } from 'react'
import { Button, Typography } from '@s4l/ds'
import { IconInfo } from '@s4l/icons/IconInfo'
import styles from '../styles.module.css'
import TaxFeesDetails from '../TaxFeesDetails'
import { type RoomData } from '../types'
import { groupTaxDetails, getTotalTaxAmount, getAllTaxDetails } from '../utils'
import { usePriceDetailsHelpers } from '../hooks'

export interface RoomSectionProps {
  room: RoomData
  index: number
  nights: number
  isMultiRoom: boolean
  isNewExperimentVariant?: boolean
}

export default function RoomSection({
  room,
  index,
  nights,
  isMultiRoom,
  isNewExperimentVariant,
}: Readonly<RoomSectionProps>) {
  const { getTaxDisplayName, formatCurrency, formatMessage } = usePriceDetailsHelpers()

  const roomName = isMultiRoom
    ? `${formatMessage({ id: 'global.room' })} ${index + 1}:`
    : formatMessage({ id: 'global.your-room' })

  const pricePerNight = room.subtotal / nights
  const pricePerNightFormatted = formatCurrency(pricePerNight, room.subtotalCurrency)

  const allTaxDetails = useMemo(
    () => getAllTaxDetails(room.taxDetails, room.additionalFees),
    [room.taxDetails, room.additionalFees]
  )
  const groupedTaxDetails = useMemo(() => groupTaxDetails(allTaxDetails), [allTaxDetails])

  // Only show tax section if we have taxes AND they have actual amounts > 0
  const hasTaxDetails =
    groupedTaxDetails.length > 0 &&
    groupedTaxDetails.some(tax => (tax.client?.amount ?? tax.amount) > 0)

  return (
    <div className={styles.room}>
      <div className={styles.roomHeader}>
        <div className={styles.titleWithDetails}>
          <Typography variant="body" size="l" bold>
            {roomName}
          </Typography>
          <Typography variant="body" size="l">
            {`${pricePerNightFormatted} x ${formatMessage({ id: 'global.nights' }, { nights })}`}
          </Typography>
        </div>
        <Typography variant="body" size="l">
          {formatCurrency(room.subtotal, room.subtotalCurrency)}
        </Typography>
      </div>

      {hasTaxDetails ? (
        <div className={styles.detailsBlock}>
          <div className={styles.detailsRowTitle}>
            <TaxFeesDetails taxDetails={groupedTaxDetails}>
              <Button variant="text-flat" size="extraSmall" trailingIcon={<IconInfo size={16} />}>
                <Typography variant="body" size="l" bold>
                  {formatMessage({ id: 'global.taxes-fees' })}
                </Typography>
              </Button>
            </TaxFeesDetails>
            <Typography variant="body" size="l">
              {formatCurrency(getTotalTaxAmount(allTaxDetails), room.totalCurrency)}
            </Typography>
          </div>

          {!isNewExperimentVariant &&
            groupedTaxDetails.map(
              tax =>
                (tax.client?.amount || tax.amount > 0) && (
                  <div className={styles.detailsRow} key={tax.name}>
                    <Typography variant="caption" size="l">
                      {`${getTaxDisplayName(tax.name)} (${tax.included ? formatMessage({ id: 'tax-breakdown.pay-upon-booking' }) : formatMessage({ id: 'tax-breakdown.pay-at-hotel' })})`}
                    </Typography>
                    <Typography variant="caption" size="l">
                      {formatCurrency(
                        tax.client?.amount ?? tax.amount,
                        tax.client?.currency ?? tax.currency
                      )}
                    </Typography>
                  </div>
                )
            )}
        </div>
      ) : (
        <div className={styles.detailsBlock}>
          <div className={styles.detailsRowTitle}>
            <Typography variant="body" size="l">
              {formatMessage({ id: 'global.taxes-fees' })}
            </Typography>
            <Typography variant="body" size="l">
              {formatCurrency(0, room.totalCurrency)}
            </Typography>
          </div>
        </div>
      )}
    </div>
  )
}

RoomSection.displayName = 'RoomSection'
